import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import ShareModal from 'components/Common/Modals/ShareModal'
import OfferBNPLAd from '../OfferBNPLAd'
import BundleAndSaveUSPs from './BundleAndSaveUSPs'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import ShareButton from 'components/Common/ShareWithFriendsButton'
import BundleAndSaveSummary from './BundleAndSaveSummary'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { shouldShowSalesUSP } from 'selectors/featuresSelectors'
import ContactUSP from '../ContactUSP'
import BundleAndSavePropertyDetails from './BundleAndSavePropertyDetails'
import type { ShareConfig } from 'components/Common/Modals/ShareModal/ShareModal'
import Group from 'components/utils/Group'
import OfferPageLabelGroup from '../OfferPageLabelGroup'
import formatBundleLocationCaption from 'lib/offer/formatBundleLocationCaption'

const OfferIntroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr min-content;
    grid-gap: ${rem(16)} ${rem(48)};
  }
`

const ContentColumn = styled.div`
  > * + * {
    margin-top: ${rem(16)};
  }
`

const SummaryColumn = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    display: flex;
    flex-direction: column;
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const SummaryBlock = styled.div`
  ${mediaQueryUp.tablet} {
    position: sticky;
    top: ${rem(12)};
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const HotelSummary = styled(BundleAndSaveSummary)`
  min-width: ${rem(308)};

  ${mediaQueryUp.desktop} {
    min-width: ${rem(382)};
  }
`

const OfferIntroWrapper = styled(LayoutContainer)`
  padding-top: ${rem(20)};
  padding-bottom: ${rem(20)};

  ${mediaQueryUp.desktop} {
    padding-top: ${rem(28)};
    padding-bottom: ${rem(28)};
  }
`

interface Props {
  offer: App.BundleOffer
  className?: string
  onViewPackageOptions: () => void
  shouldShowSalesPhoneUSP: boolean
  shareConfig: ShareConfig
}

function BundleAndSaveIntro(props: Props) {
  const intl = useIntl()
  const {
    offer,
    className,
    onViewPackageOptions,
    shouldShowSalesPhoneUSP,
    shareConfig,
  } = props

  const bundledOffers = useMemo(() => Object.values(offer.bundledOffers), [offer.bundledOffers])

  // Keep bundled destinations compact while still using a locale-aware conjunction.
  const locationCaption = useMemo(
    () => formatBundleLocationCaption(
      intl,
      bundledOffers.map((bundledOffer) => ({
        locationHeading: bundledOffer.locationHeading,
        locationSubheading: bundledOffer.locationSubheading,
      })),
    ),
    [bundledOffers, intl],
  )

  const allPrices = useMemo(() => offer.packages.map(p => p.price), [offer.packages])

  return (
    <OfferIntroWrapper>
      <OfferIntroGrid className={className} data-testid="offer-intro">
        <ContentColumn>
          <OfferPageLabelGroup offer={offer} />
          <BundleAndSavePropertyDetails
            offer={offer}
            locationCaption={locationCaption}
            bundledOffers={bundledOffers}
          />
          <BundleAndSaveUSPs offer={offer} />
        </ContentColumn>
        <SummaryColumn>
          <CSSBreakpoint min="tablet">
            <Group direction="horizontal" verticalAlign="center" gap={8} horizontalAlign="end">
              <ShareModal shareConfig={shareConfig} openOnHash="#share">
                <ShareButton horizontalOutdent="end" />
              </ShareModal>
            </Group>
          </CSSBreakpoint>
          <SummaryBlock>
            <div>
              {offer.packages.length > 0 && (
                <HotelSummary
                  onViewPackageOptions={onViewPackageOptions}
                />
              )}
            </div>
            <OfferBNPLAd prices={allPrices} />
          </SummaryBlock>
        </SummaryColumn>
        {shouldShowSalesPhoneUSP && <ContactUSP />}
      </OfferIntroGrid>
    </OfferIntroWrapper>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  shouldShowSalesPhoneUSP: shouldShowSalesUSP(appState, state.offer?.lowestPricePackage?.trackingPrice ?? 0, 1000),
  offer: state.offer,
  regionCode: appState.geo.currentRegionCode,
  pathname: appState.router.location.pathname,
  customerPortalURL: appState.system.customerPortalURL,
  memberId: appState.auth.account.memberId,
}), BundleAndSaveIntro)
